import axios from "api/axios";
import { Button, OutlineButton, SubtleIconA } from "components/button";
import GroupedButton from "components/button/grouped";
import ImageUploadButton, {
  UploadImage,
} from "components/button/imageUploadButton";
import { Input } from "components/input";
import DollarInputV2 from "components/input/dollarInputV2";
import SafeArea from "components/safe-area";
import { useSnackbarContext } from "components/snackbar";
import Spinner from "components/spinner";
import { useFormik } from "formik";
import { useState, useEffect, useRef, useMemo } from "react";
import { AiOutlineCamera } from "react-icons/ai";
import { FaRegStar, FaStar } from "react-icons/fa";
import { RiCloseCircleLine, RiCloseFill } from "react-icons/ri";
import { useMutation, useQuery } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { theme } from "styles/theme";
import { TAdditionalCategoriesGroups, TInventory, TInventoryForm } from "types/inventory";
import { TProduct, TProductForm } from "types/product";
import ReadLayout from "./read";
import * as Yup from "yup";
import Switch from "rc-switch";
import { useAreYouSure } from "components/areYouSure";
import { useAuthContext } from "contexts/auth";
import { TCategory } from "../categories/TCategory";
import Select from "components/react-select";
import { useTranslation } from 'react-i18next';
import { TTag } from "types/tag";
import CategoryGroupSelection from "./category-group-selection";
import Field from "components/field";
import { logInventoryItemPriceTrace } from "utils/inventoryPriceTrace";

type Props = {
  data?: TProduct | TInventory;
};

const centsToDollars = (cents: number | null | undefined): number | null =>
  typeof cents === "number" ? cents / 100 : null;

type MappedPartVendor = {
  vendor_id: string | number;
  vendor_name: string;
  vendor_item_price: number | null;
  vendor_part_number: string | null;
};

const preferVendorRow = (
  current: MappedPartVendor,
  candidate: MappedPartVendor
): MappedPartVendor => {
  if (current.vendor_item_price == null && candidate.vendor_item_price != null) {
    return candidate;
  }
  if (candidate.vendor_item_price == null && current.vendor_item_price != null) {
    return current;
  }
  return current;
};

/** One row per vendor — API can return duplicate vendor_item_details for the same vendor. */
const mapPartVendors = (
  vendors: TInventory["vendors"] | undefined
): MappedPartVendor[] => {
  const mapped =
    vendors?.map((v) => ({
      vendor_id: v.vendor_id,
      vendor_name: v.vendor_name,
      vendor_part_number: v.vendor_part_number,
      vendor_item_price:
        v.vendor_item_price != null ? v.vendor_item_price / 100 : null,
    })) ?? [];

  const byVendorId = new Map<string, MappedPartVendor>();
  for (const vendor of mapped) {
    const key = String(vendor.vendor_id);
    const existing = byVendorId.get(key);
    byVendorId.set(
      key,
      existing ? preferVendorRow(existing, vendor) : vendor
    );
  }
  return Array.from(byVendorId.values());
};

const getDefaultVendor = (
  vendors: MappedPartVendor[],
  vendorId: string | number | null | undefined
) => {
  if (!vendors.length) return null;
  if (vendorId != null) {
    const match = vendors.find((v) => String(v.vendor_id) === String(vendorId));
    if (match) return match;
  }
  return vendors.length === 1 ? vendors[0] : null;
};

const resolvePartListPrice = (
  listPriceCents: number | null | undefined,
  defaultVendor: MappedPartVendor | null
): number | null => {
  if (typeof listPriceCents === "number") {
    return listPriceCents / 100;
  }
  if (defaultVendor?.vendor_item_price != null) {
    return defaultVendor.vendor_item_price;
  }
  return null;
};

const PART_INITIAL_VALUES = {
  item_name: "",
  short_name: "",
  short_description: "",
  long_description: "",
  model: "",
  sku: "",
  list_price: null,
  fixed_unit_price: null,
  fixed_install_price: null,
  cat_id_v2: "",
  root_id: "",
  tags: [],
  special_order_item: false,
  bulk_order_item: false,
  bulk_item_description: "",
  sale_is_fixed: true,
  sale_multiplier: null,
  install_is_fixed: true,
  install_multiplier: null,
  group_only: false,
  additional_categories_groups: [],
  vendors: [],
  vendor_id: null,
  vendor_name: "",
  list_price_vendor_id: null,
  vendor_part_number: "",
  vendor_item_price: null,
  original_list_price: null,
};

const SERVICE_INITIAL_VALUES = {
  description: "",
  product_label: "",
  short_name: "",
  long_description: "",
  commission: true,
  list_price: null,
  fixed_unit_price: null,
  fixed_install_price: null,
  sale_is_fixed: true,
  sale_multiplier: null,
  install_is_fixed: true,
  install_multiplier: null,
  group_only: false,
  additional_categories_groups: [],
};

const buildPartInitialValues = (data: TInventory) => {
  const vendors = mapPartVendors(data?.vendors);
  const defaultVendor = getDefaultVendor(vendors, data?.vendor_id);
  const preferredVendorFromData = data?.vendor_id ?? null;
  const preferredVendorLinked =
    preferredVendorFromData != null
      ? vendors.find(
          (v) => String(v.vendor_id) === String(preferredVendorFromData)
        )
      : null;

  return {
    item_name: data?.item_name || "",
    short_name: data?.short_name || "",
    short_description: data?.short_description || "",
    long_description: data?.long_description || "",
    model: data?.model || "",
    sku: data?.sku || "",
    list_price: resolvePartListPrice(data?.list_price, defaultVendor),
    fixed_unit_price: centsToDollars(data?.fixed_unit_price),
    sale_is_fixed:
      data?.sale_is_fixed !== undefined ? data?.sale_is_fixed : true,
    sale_multiplier: data?.sale_multiplier ?? null,
    fixed_install_price: centsToDollars(data?.fixed_install_price),
    install_is_fixed:
      data?.install_is_fixed !== undefined ? data?.install_is_fixed : true,
    install_multiplier: data?.install_multiplier ?? null,
    cat_id_v2: data?.cat_id_v2 || "",
    root_id: data?.root_id || "",
    tags:
      data?.tags?.map((tag) => ({
        value: tag.id,
        label: tag.label,
      })) || [],
    special_order_item: data?.special_order_item || false,
    bulk_order_item: data?.bulk_order_item || false,
    bulk_item_description: data?.bulk_item_description || "",
    group_only: data?.group_only || false,
    additional_categories_groups: data?.additional_categories_groups || [],
    vendors,
    vendor_id: preferredVendorFromData,
    vendor_name:
      preferredVendorLinked?.vendor_name ?? defaultVendor?.vendor_name ?? "",
    list_price_vendor_id: preferredVendorLinked?.vendor_id ?? null,
    vendor_part_number: defaultVendor?.vendor_part_number ?? "",
    vendor_item_price: defaultVendor?.vendor_item_price ?? null,
    original_list_price: centsToDollars(data?.list_price),
  };
};

const buildServiceInitialValues = (data: TProduct) => ({
  description: data?.description || "",
  product_label: data?.product_label || "",
  commission: data?.commission ?? true,
  sale_is_fixed:
    data?.sale_is_fixed !== undefined ? data.sale_is_fixed : true,
  sale_multiplier: data?.sale_multiplier || null,
  list_price: centsToDollars(data?.list_price),
  fixed_unit_price: centsToDollars(data?.fixed_unit_price),
  install_is_fixed:
    data?.install_is_fixed !== undefined ? data.install_is_fixed : true,
  install_multiplier: data?.install_multiplier || null,
  fixed_install_price: centsToDollars(data?.fixed_install_price),
  group_only: data?.group_only || false,
  additional_categories_groups: data?.additional_categories_groups || [],
});

type ListPriceVendorOption = {
  label: string;
  value: string | number | null;
};

const buildListPriceVendorOptions = (
  vendors: MappedPartVendor[] | undefined,
  noneLabel: string
): ListPriceVendorOption[] => [
  { label: noneLabel, value: null },
  ...(vendors?.map((vendor) => ({
    label: vendor.vendor_name,
    value: vendor.vendor_id,
  })) ?? []),
];

const findListPriceVendorOption = (
  options: ListPriceVendorOption[],
  vendorId: string | number | null | undefined
): ListPriceVendorOption | null => {
  if (vendorId == null || vendorId === "") return null;
  return (
    options.find(
      (option) =>
        option.value != null && String(option.value) === String(vendorId)
    ) ?? null
  );
};

const vendorsMatch = (
  a: string | number | null | undefined,
  b: string | number | null | undefined
): boolean =>
  a != null && a !== "" && b != null && b !== "" && String(a) === String(b);

export default function Inventory(props: Props) {
  const { t } = useTranslation();
  const tBase = "views.inventories.inventory.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const [isPart, setIsPart] = useState(true);
  const [image, setImage] = useState<any>();
  const { id, type } = useParams<{ id: string; type?: string }>();
  const { showSnackbar } = useSnackbarContext(),
    history = useHistory(),
    triggerButton = useRef(() => null),
    saveButton = useRef<any>(),
    areYouSure = useAreYouSure();

  const [isEdit, setIsEdit] = useState(true);
  const [imageUploaded, setImageUploaded] = useState(false);

  // Helper function to normalize image URL
  // If the URL is relative (starts with /), prefix it with the server URL
  const getImageUrl = (imageUrl: string | null | undefined): string => {
    if (!imageUrl) return "";
    
    // If already an absolute URL (http:// or https://), return as-is
    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      return imageUrl;
    }
    
    // If it's a relative path (starts with /), prefix with server URL
    if (imageUrl.startsWith("/")) {
      const serverUrl = process.env.REACT_APP_SERVER_URL || "";
      return serverUrl ? `${serverUrl}${imageUrl}` : imageUrl;
    }
    
    // Otherwise return as-is (might be a data URL or other format)
    return imageUrl;
  };

  const handleUpload = (props: UploadImage) => {
    setImage(props);
    setImageUploaded(true);
    // Mark form as dirty when image is uploaded
    formik.setFieldTouched('image', true);
    formik.setFieldValue('image', props);
  };

  const [archiver, setArchiver] = useState<{
    archived_by: string;
    archived_at: Date;
  } | null>(null);

  useEffect(() => {
    if (type === "part") setIsPart(true);
    if (type === "service") setIsPart(false);
    if (type === "group") setIsPart(false);
  }, [type]);

  useEffect(() => {
    getArchiver();
  }, []);

  const query = useQuery<
    ({ type: "part" } & TInventory) | ({ type: "service" } & TProduct)
  >(
    ["item", id, type],
    async () =>
      id !== "new" &&
      (await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/item/${type}/${id}
  `
        )
        .then((res) => {
          const data = { ...res.data, type };
          if (type === "part") {
            logInventoryItemPriceTrace("GET /eserv-business/item/part/:id", {
              itemId: id,
              list_price: data.list_price,
              vendor_id: data.vendor_id,
              vendors: data.vendors,
            });
          }
          return data;
        })),
    { refetchOnWindowFocus: false }
  );

  const mutation = useMutation(
    async (form: any) =>
      await axios
        .post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/item/update`,
          form,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
        .then((res) => res.data)
  );

  let { name } = useAuthContext();

  const [isArchiving, setIsArchiving] = useState(false);

  const Archive = async (toggle: boolean) => {
    try {
      setIsArchiving(true);

      await axios
        .post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/item/archive/${id}?type=${type}&archive=${toggle}&name=${name}`
        )
        .then(() => query.refetch());

      getArchiver();

    } finally {
      setIsArchiving(false);
    }
  };

  const getArchiver = async () => {
    await axios
      .get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/item?type=ARCHIVER&item_id=${id}&item_type=${type}`
      )
      .then((res) => setArchiver(res.data[0]));
  };

  const initialValues = useMemo(() => {
    if (!query.data) {
      return isPart ? PART_INITIAL_VALUES : SERVICE_INITIAL_VALUES;
    }

    if (query.data.type === "part") {
      return buildPartInitialValues(query.data as TInventory);
    }

    return buildServiceInitialValues(query.data as TProduct);
  }, [query.data, isPart]);

  const onSubmit = async (
    e: TProductForm | (TInventoryForm & { t?: boolean })
  ) => {
    try {
      const tags = e.tags ? e.tags.map((tag) => tag.value) : [];
      const {
        original_list_price: _originalListPrice,
        list_price_vendor_id: _listPriceVendorId,
        vendor_name: _vendorName,
        vendor_part_number: _vendorPartNumber,
        vendor_item_price: _vendorItemPrice,
        vendors: _vendors,
        vendor_id: submittedVendorId,
        ...formFields
      } = e as TInventoryForm & {
        original_list_price?: number | null;
        list_price_vendor_id?: string | number | null;
        vendor_name?: string;
        vendor_part_number?: string;
        vendor_item_price?: number | null;
        vendors?: MappedPartVendor[];
        vendor_id?: string | number | null;
      };

      const formData = new FormData();
      const preferredVendorId =
        submittedVendorId === undefined ||
        submittedVendorId === null ||
        submittedVendorId === ""
          ? null
          : submittedVendorId;

      formData.append(
        "form",
        JSON.stringify({
          ...formFields,
          vendor_id: preferredVendorId,
          tags,
          isPart,
          id,
          list_price:
            typeof e.list_price === "number"
              ? Math.round(e.list_price * 100)
              : null,
          fixed_unit_price:
            typeof e.fixed_unit_price === "number"
              ? Math.round(e.fixed_unit_price * 100)
              : null,
          fixed_install_price:
            typeof e.fixed_install_price === "number"
              ? Math.round(e.fixed_install_price * 100)
              : null,
        })
      );

      if (image) {
        // const imageBlob = await (await fetch(image)).blob();
        formData.append("image", image.blob);
      }

      const res = await mutation.mutateAsync(formData);

      return res;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const validationSchema = isPart
    ? Yup.object({
      item_name: Yup.string().required(),
      short_name: Yup.string(),
      short_description: Yup.string(),
      long_description: Yup.string(),
      model: Yup.string(),
      sku: Yup.string(),
      list_price: Yup.number().nullable(),
      fixed_unit_price: Yup.number().nullable(),
      fixed_install_price: Yup.number().nullable(),
      cat_id_v2: Yup.string(),
      root_id: Yup.string(),
      tags: Yup.array().of(
        Yup.object().shape({
          value: Yup.number().required(),
          label: Yup.string().required(),
        })
      ).nullable(),
      special_order_item: Yup.boolean(),
      bulk_order_item: Yup.boolean(),
      bulk_item_description: Yup.string(),
      sale_is_fixed: Yup.boolean(),
      // sale_multiplier: Yup.number().nullable(),
      install_is_fixed: Yup.boolean(),
      // install_multiplier: Yup.number().nullable(),
      group_only: Yup.boolean(),
      additional_categories_groups: Yup.array().of(
        Yup.object().shape({
          fk_category_id: Yup.string().required(),
          fk_group_id: Yup.string().required(),
        })
      ),
      vendors: Yup.array().of(
        Yup.object().shape({
          vendor_id: Yup.string().required(),
          vendor_name: Yup.string().required(),
          vendor_item_price: Yup.number().nullable(),
          vendor_part_number: Yup.string().nullable(),
        })
      ),
    })
    : Yup.object({
      description: Yup.string(),
      product_label: Yup.string().required(),
      commission: Yup.boolean().required(),
      list_price: Yup.number().nullable(),
      fixed_unit_price: Yup.number().nullable(),
      fixed_install_price: Yup.number().nullable(),
      sale_is_fixed: Yup.boolean(),
      sale_multiplier: Yup.number().nullable(),
      install_is_fixed: Yup.boolean(),
      install_multiplier: Yup.number().nullable(),
      group_only: Yup.boolean(),
      additional_categories_groups: Yup.array().of(
        Yup.object().shape({
          fk_category_id: Yup.string().required(),
          fk_group_id: Yup.string().required(),
        })
      ),
    });

  const formik = useFormik<TInventoryForm | TProductForm | any>({
    initialValues,
    enableReinitialize: false,
    onSubmit,
    validationSchema,
  });

  useEffect(() => {
    if (!query.data || formik.dirty) return;

    const nextValues =
      query.data.type === "part"
        ? buildPartInitialValues(query.data as TInventory)
        : buildServiceInitialValues(query.data as TProduct);

    formik.resetForm({ values: nextValues });
  }, [query.data, formik.dirty]);

  const submit = async (anotherOne: boolean) => {
    try {
      const result = await formik.submitForm();
      if (!result || !(result as { type?: string }).type) return;

      showSnackbar(id !== "new" ? "Item updated!" : "Item added!");
      if (id === "new") {
        anotherOne
          ? formik.resetForm()
          : history.push(
              `/inventory/${(result as { id: string | number }).id}/${
                (result as { type: string }).type
              }`
            );
      } else {
        const { data: refreshedItem } = await query.refetch();
        if (refreshedItem) {
          const nextValues =
            refreshedItem.type === "part"
              ? buildPartInitialValues(refreshedItem as TInventory)
              : buildServiceInitialValues(refreshedItem as TProduct);
          formik.resetForm({ values: nextValues });
        }
        setImageUploaded(false);
        setImage(undefined);
      }
    } catch (e: any) {
      console.error(e);
      const message =
        e?.response?.status === 403
          ? tr("No permission to update this item")
          : e?.response?.data?.message || tr("Error saving item");
      showSnackbar(message);
    }
  };




  if (id !== "new" && query.isLoading) return <Spinner />;
  return (
    <div style={{ position: "relative" }}>
      <div
        className={`px-2 py-4 sticky top-0 b-b flex !z-${theme.zIndex.navigation
          } border-b-2 bg-white items-center ${query.data?.is_voided && "border-red-600 bg-red-100"
          }`}
        style={{ zIndex: theme.zIndex.navigation }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <SubtleIconA to="/products" size="small">
            <RiCloseFill />
          </SubtleIconA>
          | {tr("Edit Product")}
        </div>
        {query.data?.is_voided && (
          <div>
            <div
              className="p-1 mx-2 text-sm font-bold text-white uppercase bg-red-600 rounded"
              style={{ display: "inline-block" }}
            >
              {tr("Archived")}
            </div>
            <div
              style={{
                display: "inline-block",
                color: "red",
                fontStyle: "italic",
              }}
            >
              {/* Note: existing archived items will not have this shown */}
              {archiver?.archived_by &&
                `${tr("Archived By")}: ` +
                (archiver.archived_by || "User") +
                ` ${tr("on")} ` +
                new Date(archiver.archived_at).toLocaleString("default", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
            </div>
          </div>
        )}
        <div className="flex items-center ml-auto">
          {id !== "new" && !query.data?.is_voided && (
          <OutlineButton
            className="!border-red-600 !text-red-600"
            onClick={() => Archive(true)}
            disabled={isArchiving}
          >
            {isArchiving ? (
              <>
                <Spinner />
                {tr("Archiving...")}
              </>
            ) : (
              <>
                <RiCloseCircleLine />
                {tr("Archive")}
              </>
            )}
          </OutlineButton>
          )}
          {id !== "new" && query.data?.is_voided && (
            <OutlineButton
              className="!border-green-600 !text-green-600"
              onClick={() => Archive(false)}
              disabled={isArchiving}
            >
              {isArchiving ? (
                <>
                  <Spinner />
                  {tr("Unarchiving...")}
                </>
              ) : (
                <>
                  <RiCloseCircleLine /> {tr("Unarchive")}
                </>
              )}
            </OutlineButton>
          )}
          {id === "new" && (
            <OutlineButton
              primary
              // size="small"
              type="submit"
              disabled={(!formik.dirty && !imageUploaded) || !formik.isValid}
              onClick={() => submit(true)}
              isLoading={mutation.isLoading}
            >
              {tr("Save and Add Another")}
            </OutlineButton>
          )}

          <Button
            primary
            // size="small"
            style={{ marginLeft: "1rem" }}
            type="submit"
            ref={saveButton}
            disabled={(!formik.dirty && !imageUploaded) || !formik.isValid}
            onClick={() => submit(false)}
            isLoading={mutation.isLoading}
          >
            {tr("Save Changes")}
          </Button>
        </div>
      </div>
      <SafeArea
        style={{
          content: {
            display: "flex",
            maxWidth: "700px",
            padding: "1rem 0",
            flexDirection: "column",
          },
        }}
      >
        {isEdit ? (
          <>
            <ImageUploadButton
              uploadImages={handleUpload}
              Component={
                <div
                  style={{
                    width: "200px",
                    height: "200px",
                    fontSize: "2rem",
                    border: `1px solid ${theme.border.light}`,
                    alignSelf: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    background: "white",
                    borderRadius: "10px",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={image?.dataUrl || (query.data?.imageUrl ? getImageUrl(query.data.imageUrl) : "")}
                    style={{ width: "100%", maxWidth: "100%" }}
                  />
                  <AiOutlineCamera style={{ position: "absolute" }} />
                </div>
              }
            />
            {type ? (
              <h1 style={{ textTransform: "capitalize" }}>{type}</h1>
            ) : (
              <GroupedButton style={{ margin: "1rem 0" }}>
                <Button $selected={isPart} onClick={() => setIsPart(true)}>
                  {tr("Part")}
                </Button>
                <Button $selected={!isPart} onClick={() => setIsPart(false)}>
                  {tr("Services")}
                </Button>
              </GroupedButton>
            )}

            {isPart ? (
              <PartForm {...{ formik }} />
            ) : (
              <ServiceForm {...{ formik }} />
            )}
          </>
        ) : (
          <ReadLayout data={query.data} isPart={isPart} />
        )}
      </SafeArea>
    </div>
  );
}

type PartFormProps = {
  formik: any;
};

// const fetchCategories = async (): Promise<TCategory[]> => {
//   const { data } = await axios.get<TCategory[]>(
//     `${process.env.REACT_APP_SERVER_URL}/api/v1/company/products/categories`
//   );
//   return data;
// };

const PartForm = ({ formik }: PartFormProps) => {
  const { t } = useTranslation();
  const tBase = "views.inventories.inventory.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  // preserves the original list price when the vendor is changed, used for changing the vendor back to "None"
  const originalListPriceRef = useRef<number | null>(null);
  // remembers preferred vendor cleared by choosing "None" so re-selecting restores the star
  const lastPreferredBeforeClearRef = useRef<string | number | null>(null);

  // Inventory-only list price (before vendor sync), used when vendor is set back to "None"
  useEffect(() => {
    originalListPriceRef.current = formik.values.original_list_price ?? null;
  }, [formik.values.original_list_price]);

  const {
    data: categories,
    isError,
    isLoading,
    error,
  } = useQuery<TCategory[], Error>(
    "categories",
    async () =>
      await axios
        .get<TCategory[]>(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/products/categories`
        )
        .then((res) => res.data)
  );

  const handleCategoryChange = (selectedOption: any) => {
    if (!selectedOption) {
      // Handle the case when the option is null or undefined, e.g., reset the value
      formik.setValues((v: any) => ({
        ...v,
        root_id: "", // Reset main category
        cat_id_v2: "", // Reset subcategory
      }));
      return;
    }

    const { value } = selectedOption;
    formik.setValues((v: any) => ({
      ...v,
      root_id: value,
      cat_id_v2: "", // Reset subcategory
    }));
  };

  const handleSubCategoryChange = (selectedOption: any) => {
    if (!selectedOption) {
      // Handle the case when the option is null or undefined
      formik.setValues((v: any) => ({
        ...v,
        cat_id_v2: "",  // or null, depending on your initial state
      }));
      return;
    }

    const { value } = selectedOption;
    formik.setValues((v: any) => ({
      ...v,
      cat_id_v2: value,
    }));
  };

  const handleChangeSpecialOrder = (value: boolean) => {
    formik.setValues((v: any) => ({ ...v, special_order_item: value }));
  };

  const handleChangeBulk = (value: boolean) => {
    formik.setValues((v: any) => ({ ...v, bulk_order_item: value }));
  };

  const handleChangeGroupOnly = (value: boolean) => {
    formik.setValues((v: any) => ({ ...v, group_only: value }));
  };

  const tagsQuery = useQuery<Array<TTag>>("tags", async () =>
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/tags/inventory`)
      .then((res) => res.data)
  );

  const tagOptions = tagsQuery.data?.map((tag) => ({ value: tag.id, label: tag.label })) || [];

  const listPriceVendorOptions = useMemo(() => {
    const noneLabel = t(`${tBase}.None`);
    return buildListPriceVendorOptions(formik.values.vendors, noneLabel);
  }, [formik.values.vendors, t, tBase]);

  const selectedListPriceVendorOption = useMemo(
    () =>
      findListPriceVendorOption(
        listPriceVendorOptions,
        formik.values.list_price_vendor_id
      ),
    [listPriceVendorOptions, formik.values.list_price_vendor_id]
  );

  const hasPreferredVendor =
    formik.values.vendor_id != null && formik.values.vendor_id !== "";

  const isListPriceVendorPreferred = vendorsMatch(
    formik.values.vendor_id,
    formik.values.list_price_vendor_id
  );

  const canTogglePreferredVendor =
    !!formik.values.list_price_vendor_id || hasPreferredVendor;

  const togglePreferredVendor = () => {
    if (!canTogglePreferredVendor) return;

    if (isListPriceVendorPreferred) {
      lastPreferredBeforeClearRef.current = null;
      formik.setValues({
        ...formik.values,
        vendor_id: null,
        vendor_name: "",
      });
      return;
    }

    if (!formik.values.list_price_vendor_id) return;

    const selectedVendor = formik.values.vendors?.find((vendor: MappedPartVendor) =>
      vendorsMatch(vendor.vendor_id, formik.values.list_price_vendor_id)
    );

    if (!selectedVendor) return;

    lastPreferredBeforeClearRef.current = null;
    formik.setValues({
      ...formik.values,
      vendor_id: selectedVendor.vendor_id,
      vendor_name: selectedVendor.vendor_name,
    });
  };

  const handleListPriceVendorChange = (
    selectedOption: ListPriceVendorOption | null
  ) => {
    if (!selectedOption?.value) {
      if (vendorsMatch(formik.values.vendor_id, formik.values.list_price_vendor_id)) {
        lastPreferredBeforeClearRef.current = formik.values.vendor_id;
      }
      formik.setValues({
        ...formik.values,
        list_price_vendor_id: null,
        vendor_part_number: "",
        vendor_item_price: "",
        list_price: originalListPriceRef.current,
        vendor_id: null,
        vendor_name: "",
      });
      return;
    }

    const selectedVendor = formik.values.vendors?.find((vendor: MappedPartVendor) =>
      vendorsMatch(vendor.vendor_id, selectedOption.value)
    );

    if (!selectedVendor) return;

    const previousListPriceVendorId = formik.values.list_price_vendor_id;

    // Remember preferred vendor when list-price source changes away from it
    if (
      !vendorsMatch(selectedVendor.vendor_id, previousListPriceVendorId) &&
      vendorsMatch(formik.values.vendor_id, previousListPriceVendorId)
    ) {
      lastPreferredBeforeClearRef.current = formik.values.vendor_id;
    }

    const restorePreferred =
      vendorsMatch(lastPreferredBeforeClearRef.current, selectedVendor.vendor_id) ||
      vendorsMatch(formik.values.vendor_id, selectedVendor.vendor_id);

    if (restorePreferred) {
      lastPreferredBeforeClearRef.current = null;
    }

    formik.setValues({
      ...formik.values,
      list_price_vendor_id: selectedVendor.vendor_id,
      vendor_part_number: selectedVendor.vendor_part_number ?? "",
      vendor_item_price: selectedVendor.vendor_item_price,
      list_price: selectedVendor.vendor_item_price,
      vendor_id: restorePreferred ? selectedVendor.vendor_id : null,
      vendor_name: restorePreferred ? selectedVendor.vendor_name : "",
    });
  };

  if (isLoading) return <Spinner />;
  if (isError) return <div>{tr("Error fetching categories")}</div>;

  return (
    <SForm onSubmit={formik.handleSubmit}>
      <Input label={tr("Item Name")} name="item_name" required formik={formik} />
      <Input label={tr("Short Name")} name="short_name" formik={formik} />
      <Input
        name="short_description"
        label={tr("Short Description")}
        formik={formik}
      />
      <Input label={tr("Long Description")} name="long_description" formik={formik} />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Input
          style={{ width: "49%" }}
          name="model"
          label={tr("Model")}
          formik={formik}
        />
        <Input
          label="SKU"
          style={{ width: "49%" }}
          name="sku"
          formik={formik}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Select
          style={{ width: "49%" }}
          name="root_id"
          label={tr("Category")}
          options={
            categories
              ? categories
                .filter((cat) => cat.is_parent_category)
                .map((cat) => ({
                  label: cat.category_name,
                  value: cat.category_id,
                }))
              : []
          }
          onChange={handleCategoryChange}
          value={
            formik.values.root_id
              ? {
                label: categories?.find(
                  (cat) =>
                    Number(cat.category_id) === Number(formik.values.root_id)
                )?.category_name,
                value: formik.values.root_id,
              }
              : undefined
          }
        />
        <Select
          name="cat_id_v2"
          label={tr("Sub Category")}
          options={
            formik.values.root_id && categories
              ? categories
                .filter(
                  (cat) =>
                    Number(cat.parent_category_id) ===
                    Number(formik.values.root_id)
                )
                .map((cat) => ({
                  label: cat.category_name,
                  value: cat.category_id,
                }))
              : []
          }
          onChange={handleSubCategoryChange}
          style={{
            width: "49%",
          }}
          value={
            formik.values.cat_id_v2
              ? {
                label: categories?.find(
                  (cat) =>
                    Number(cat.category_id) === Number(formik.values.cat_id_v2)
                )?.category_name,
                value: formik.values.cat_id_v2,
              }
              : null
          }
        />
      </div>

      <CategoryGroupSelection
        categories={categories || []}
        itemId={formik.values.id}
        additionalCategoriesGroups={formik.values.additional_categories_groups}
        setAdditionalCategoriesGroups={(value: TAdditionalCategoriesGroups[]) =>
          formik.setFieldValue("additional_categories_groups", value)
        }
      />


      <div
        style={{
          zIndex: 1000,
        }}
      >
        <Select
          isMulti
          name="tags"
          options={tagOptions}
          className="basic-multi-select"
          label={tr("Tags")}
          // classNamePrefix="select"
          value={formik.values.tags}
          onChange={(selectedOptions) => {
            const tags = selectedOptions
              ? selectedOptions.map((option: { value: any; label: any; }) => ({
                value: option.value,
                label: option.label,
              }))
              : [];
            formik.setFieldValue("tags", tags);
            formik.setFieldTouched("tags", true);
          }}
          placeholder={tr("Select tags")}
        />
      </div>

      <div className="flex flex-row items-center">
        <Switch
          onChange={(e) => handleChangeBulk(e)}
          checked={formik.values.bulk_order_item}
        />
        <label className="ml-2">{tr("Bulk Order Item")}</label>
      </div>

      <div className="flex flex-row items-center">
        <Switch
          onChange={(e) => handleChangeSpecialOrder(e)}
          checked={formik.values.special_order_item}
        />
        <label className="ml-2">{tr("Special Order Item")}</label>
      </div>

      <div className="flex flex-row items-center">
        <Switch
          onChange={(e) => handleChangeGroupOnly(e)}
          checked={formik.values.group_only}
        />
        <label className="ml-2">{tr("Group Only")}</label>
      </div>
      {/* {!bulkOrderDescHidden && (
        <Input
          label="Bulk Order Item Description"
          name="bulk_item_description"
          type="text"
        />
      )} */}
      {/* <Select name="tags" creatable isMulti options={tagBox} label="Tags" /> */}



      <div className="">
        <h2>{tr("Price Type")}</h2>
      </div>
      <div className="border rounded-lg">
        <div className="divide-y">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className="p-4 w-[100%]">
              <h4>{tr("List Price")}</h4>
              <DollarInputV2
                name="list_price"
                style={{ width: "100%" }}
                formik={formik}
              />
            </div>
            <div className="p-4 w-[100%]">
              <h4>{tr("List Price Vendor")}</h4>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <Field
                    type="select"
                    name="list_price_vendor_id"
                    value={selectedListPriceVendorOption}
                    options={listPriceVendorOptions}
                    onChange={handleListPriceVendorChange}
                  />
                </div>
                <div className="relative flex-shrink-0 group">
                  <button
                    type="button"
                    aria-pressed={isListPriceVendorPreferred}
                    aria-label={
                      isListPriceVendorPreferred
                        ? tr("Remove preferred vendor tooltip")
                        : tr("Set preferred vendor tooltip")
                    }
                    disabled={!canTogglePreferredVendor}
                    onClick={togglePreferredVendor}
                    className={`p-2 rounded-md border transition-colors ${
                      !canTogglePreferredVendor
                        ? "border-gray-200 text-gray-300 cursor-not-allowed"
                        : isListPriceVendorPreferred
                          ? "border-orange-400 bg-orange-50 text-orange-500 hover:bg-orange-100"
                          : "border-gray-300 text-gray-400 hover:border-orange-300 hover:text-orange-500 hover:bg-orange-50"
                    }`}
                  >
                    {isListPriceVendorPreferred ? (
                      <FaStar size={18} />
                    ) : (
                      <FaRegStar size={18} />
                    )}
                  </button>
                  {canTogglePreferredVendor ? (
                    <div
                      role="tooltip"
                      className="pointer-events-none absolute bottom-[calc(100%+6px)] left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-[5px] bg-gray-700 px-2 py-1 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible"
                    >
                      {isListPriceVendorPreferred
                        ? tr("Remove preferred vendor tooltip")
                        : tr("Set preferred vendor tooltip")}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="flex justify-between">
              <h4>{tr("Sale Price")}</h4>
              <div>
                <OutlineButton
                  size="small"
                  className={`mr-1 ${formik.values.sale_is_fixed && "!bg-primary !text-white"
                    }`}
                  onClick={() =>
                    formik.setValues((v: any) => ({
                      ...v,
                      sale_is_fixed: true,
                    }))
                  }
                >
                  {tr("Fixed")}
                </OutlineButton>
                <OutlineButton
                  size="small"
                  className={`mr-1 ${!formik.values.sale_is_fixed && "!bg-primary !text-white"
                    }`}
                  onClick={() =>
                    formik.setValues((v: any) => ({
                      ...v,
                      sale_is_fixed: false,
                    }))
                  }
                >
                  {tr("Multiply")}
                </OutlineButton>
              </div>
            </div>
            <div className="flex items-end justify-between space-x-2">
              {!formik.values.sale_is_fixed ? (
                <>
                  <DollarInputV2
                    name="list_price"
                    style={{ width: "35%" }}
                    label={tr("List Price")}
                    disabled
                    formik={formik}
                  />
                  <span className="leading-10">x</span>
                  <Input
                    name="sale_multiplier"
                    label={tr("Multiplier")}
                    style={{ width: "20%" }}
                    formik={formik}
                    type="number"
                  />
                  <span className="leading-10">=</span>
                  <DollarInputV2
                    label={tr("Sale Price")}
                    style={{ width: "35%" }}
                    value={
                      (formik.values.sale_multiplier || 0) *
                      (formik.values.list_price || 0)
                    }
                    disabled
                  />
                </>
              ) : (
                <DollarInputV2
                  name="fixed_unit_price"
                  style={{ width: "50%" }}
                  formik={formik}
                />
              )}
            </div>
          </div>
          <div className="p-4">
            <div className="flex justify-between">
              <h4>{tr("Installed Price")}</h4>
              <div>
                <OutlineButton
                  size="small"
                  className={`mr-1 ${formik.values.install_is_fixed && "!bg-primary !text-white"
                    }`}
                  onClick={() =>
                    formik.setValues((v: any) => ({
                      ...v,
                      install_is_fixed: true,
                    }))
                  }
                >
                  {tr("Fixed")}
                </OutlineButton>
                <OutlineButton
                  size="small"
                  className={`mr-1 ${!formik.values.install_is_fixed && "!bg-primary !text-white"
                    }`}
                  onClick={() =>
                    formik.setValues((v: any) => ({
                      ...v,
                      install_is_fixed: false,
                    }))
                  }
                >
                  {tr("Multiply")}
                </OutlineButton>
              </div>
            </div>

            <div className="flex items-end justify-between space-x-2">
              {!formik.values.install_is_fixed ? (
                <>
                  <DollarInputV2
                    name="list_price"
                    style={{ width: "35%" }}
                    label={tr("List Price")}
                    disabled
                    formik={formik}
                  />
                  <span className="leading-10">x</span>
                  <Input
                    name="install_multiplier"
                    label={tr("Multiplier")}
                    style={{ width: "20%" }}
                    formik={formik}
                    type="number"
                  />
                  <span className="leading-10">=</span>
                  <DollarInputV2
                    label={tr("Installed Price")}
                    style={{ width: "35%" }}
                    value={
                      (formik.values.install_multiplier || 0) *
                      (formik.values.list_price || 0)
                    }
                    disabled
                  />
                </>
              ) : (
                <DollarInputV2
                  name="fixed_install_price"
                  style={{ width: "50%" }}
                  formik={formik}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </SForm>
  );
};

type ServiceFormProps = {
  formik: any;
};

const ServiceForm = ({ formik }: ServiceFormProps) => {
  const { t } = useTranslation();
  const tBase = "views.inventories.inventory.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const handleChangeGroupOnly = (value: boolean) => {
    formik.setValues((v: any) => ({ ...v, group_only: value }));
  };

  return (
    <SForm onSubmit={formik.handleSubmit}>
      {/* <Input name="product_label" label={tr("Label")} formik={formik} required />
      <Input name="description" label={tr("Description")} formik={formik} /> */}
      <div>
        <div className="text-[12.8px] mb-1">{tr("Label")}</div>
        <textarea 
          className="w-full border rounded-md -mb-2 min-h-[40px]"
          name="product_label" 
          value={formik.values.product_label}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          rows={1}
          required={true}
        />
      </div>
      <div>
        <div className="text-[12.8px] mb-1">{tr("Description")}</div>
        <textarea 
          className="w-full border rounded-md -mb-2 min-h-[40px]"
          name="description" 
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          rows={1}
          required={true}
        />
      </div>
      <div className="flex flex-row items-center">
        <Switch
          onChange={(e) => handleChangeGroupOnly(e)}
          checked={formik.values.group_only}
        />
        <label className="ml-2">{tr("Group Only")}</label>
      </div>
      <div className="">
        <h2>{tr("Price Type")}</h2>
      </div>
      <div className="border rounded-lg">
        <div className="divide-y">
          <div className="p-4">
            <h4>{tr("List Price")}</h4>
            <DollarInputV2
              name="list_price"
              style={{ width: "50%" }}
              formik={formik}
            // value={formik.values.list_price}
            />
          </div>

          <div className="p-4">
            <div className="flex justify-between">
              <h4>{tr("Sale Price")}</h4>
              <div>
                <OutlineButton
                  size="small"
                  className={`mr-1 ${formik.values.sale_is_fixed && "!bg-primary !text-white"
                    }`}
                  onClick={() =>
                    formik.setValues((v: any) => ({
                      ...v,
                      sale_is_fixed: true,
                    }))
                  }
                >
                  {tr("Fixed")}
                </OutlineButton>
                <OutlineButton
                  size="small"
                  className={`mr-1 ${!formik.values.sale_is_fixed && "!bg-primary !text-white"
                    }`}
                  onClick={() =>
                    formik.setValues((v: any) => ({
                      ...v,
                      sale_is_fixed: false,
                    }))
                  }
                >
                  {tr("Multiply")}
                </OutlineButton>
              </div>
            </div>
            <div className="flex items-end justify-between space-x-2">
              {!formik.values.sale_is_fixed ? (
                <>
                  <DollarInputV2
                    name="list_price"
                    style={{ width: "35%" }}
                    label={tr("List Price")}
                    disabled
                    formik={formik}
                  />
                  <span className="leading-10">x</span>
                  <Input
                    name="sale_multiplier"
                    label={tr("Multiplier")}
                    style={{ width: "20%" }}
                    formik={formik}
                    type="number"
                  />
                  <span className="leading-10">=</span>
                  <DollarInputV2
                    label={tr("Sale Price")}
                    style={{ width: "35%" }}
                    value={
                      (formik.values.sale_multiplier || 0) *
                      (formik.values.list_price || 0)
                    }
                    disabled
                  />
                </>
              ) : (
                <DollarInputV2
                  name="fixed_unit_price"
                  style={{ width: "50%" }}
                  formik={formik}
                />
              )}
            </div>
          </div>
          <div className="p-4">
            <div className="flex justify-between">
              <h4>{tr("Installed Price")}</h4>
              <div>
                <OutlineButton
                  size="small"
                  className={`mr-1 ${formik.values.install_is_fixed && "!bg-primary !text-white"
                    }`}
                  onClick={() =>
                    formik.setValues((v: any) => ({
                      ...v,
                      install_is_fixed: true,
                    }))
                  }
                >
                  {tr("Fixed")}
                </OutlineButton>
                <OutlineButton
                  size="small"
                  className={`mr-1 ${!formik.values.install_is_fixed && "!bg-primary !text-white"
                    }`}
                  onClick={() =>
                    formik.setValues((v: any) => ({
                      ...v,
                      install_is_fixed: false,
                    }))
                  }
                >
                  {tr("Multiply")}
                </OutlineButton>
              </div>
            </div>

            <div className="flex items-end justify-between space-x-2">
              {!formik.values.install_is_fixed ? (
                <>
                  <DollarInputV2
                    name="list_price"
                    style={{ width: "35%" }}
                    label={tr("List Price")}
                    disabled
                    formik={formik}
                  />
                  <span className="leading-10">x</span>
                  <Input
                    name="install_multiplier"
                    label={tr("Multiplier")}
                    style={{ width: "20%" }}
                    formik={formik}
                    type="number"
                  />
                  <span className="leading-10">=</span>
                  <DollarInputV2
                    label={tr("Installed Price")}
                    style={{ width: "35%" }}
                    value={
                      (formik.values.install_multiplier || 0) *
                      (formik.values.list_price || 0)
                    }
                    disabled
                  />
                </>
              ) : (
                <DollarInputV2
                  name="fixed_install_price"
                  style={{ width: "50%" }}
                  formik={formik}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex space-x-2">
        <span>{tr("Include in Commission")}</span>
        <Switch
          checked={formik.values.commission}
          onChange={(e) => formik.setFieldValue("commission", e)}
        />
      </div>
    </SForm>
  );
};

const SForm = styled.form`
  > * {
    margin: 1rem 0;
  }
`;
